<template>
  <div>
    <div class="row">
      <div class="col-lg-9">
        <h5 class="setting-title is-uppercase">Reasons</h5>
        <p class="text-muted">Customize the list of reasons that members can select when modifying time.</p>
      </div>
      <div class="col-lg-3 text-right">
        <button
          class="btn btn-primary add-new"
          @click="openEditReason(-1)"
        >Add reason</button>
      </div>
    </div>
    <table class="table">
      <thead>
        <tr>
          <th>Reason</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(reason, index) in reasonsData"
          :key="index"
        >
          <td>{{ reason }}</td>
          <td class="text-right">
            <div class="dropdown">
              <button
                class="btn btn-default dropdown-toggle"
                type="button"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="true"
              >
                Actions &nbsp;
                <span class="caret"></span>
              </button>
              <ul class="dropdown-menu dropdown-menu-right">
                <li><a @click="openEditReason(index)">Edit</a></li>
                <li><a @click="openDeleteReason(index)">Delete</a></li>
              </ul>
            </div>
          </td>
        </tr>

        <tr>
          <td>Other</td>
          <td class="text-right">
            <div class="dropdown">
              <button
                v-tooltip
                class="btn btn-default dropdown-toggle disabled"
                type="button"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="true"
                title="This reason cannot be modified or deleted"
              >
                Actions &nbsp;
                <span class="caret"></span>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <AppDialog
      v-if="editReasonIndex !== null"
      class="edit-reason-dialog"
      :title="isEdit ? 'Edit reason' : 'Add reason'"
      show
      append-to-body
      position="centered"
      @close="closeDialog()"
    >
      <ValidationObserver ref="formValidator">
        <ValidationProvider
          v-slot="{ errors }"
          :rules="`required|unique:${validationReasons}|length100|otherValue`"
          name="editReasonValue"
        >
          <form @submit.prevent="saveReason()">
            <div :class="{ 'has-error': showErrors && errors[0] }">
              <label class="control-label">Title*</label>
              <input
                ref="reasonRef"
                v-model="editReasonValue"
                type="text"
                class="form-control"
              />
              <AppDialogError
                v-if="showErrors"
                :errors="errors"
              />
            </div>
          </form>
        </ValidationProvider>
      </ValidationObserver>

      <template #footer>
        <div class="actions">
          <div>
            <AppButton
              type="default"
              @click="closeDialog()"
            >
              Cancel
            </AppButton>
            <AppButton
              type="primary"
              :disabled="isSaving"
              @click="saveReason()"
            >
              Save
            </AppButton>
          </div>
        </div>
      </template>
    </AppDialog>

    <AppDialog
      v-if="deleteReasonIndex !== null"
      class="delete-reason-dialog"
      title="Delete reason?"
      show
      append-to-body
      position="centered"
      @close="closeDialog()"
    >
      <p>When deleted, members will no longer be able to select this reason when modifying time. This action cannot be undone. Are you sure you want to delete it?</p>
      <template #footer>
        <div class="actions">
          <div>
            <AppButton
              type="default"
              @click="closeDialog()"
            >
              Cancel
            </AppButton>
            <AppButton
              type="danger"
              :disabled="isSaving"
              @click="deleteReason()"
            >
              Delete
            </AppButton>
          </div>
        </div>
      </template>
    </AppDialog>
  </div>
</template>

<script>
import { extend } from 'vee-validate';
import {TimesheetsResource} from "~/resources";

extend('unique', {
  validate(value, array) {
    return !array.includes(value.trim());
  },
  message: 'A reason with this title already exists. Enter a unique title.',
});

extend("length100", {
  validate(value) {
    return value.length <= 100;
  },
  message: "The reason title must be under 100 characters. Shorten it and try again. ",
});

extend("otherValue", {
  validate(value) {
    return value.toLowerCase() !== "other";
  },
  message: "Other value is reserved. Please choose another value.",
});
export default {
  props: {
    organizationId: {
      type: Number,
      required: true,
    },
    reasons: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      reasonsData: this.reasons,
      editReasonValue: '',
      editReasonIndex: null,
      deleteReasonIndex: null,
      isSaving: false,
      showErrors: false,
    };
  },
  computed: {
    isEdit() {
      return this.editReasonIndex !== -1;
    },
    validationReasons() {
      return this.isEdit ? this.reasonsData.filter((reason, index) => index !== this.editReasonIndex) : this.reasonsData;
    },
  },
  watch: {
    editReasonIndex(newVal) {
      if (newVal !== null) {
        this.$nextTick(() => {
          this.$refs.reasonRef.focus();
        });
      }
    },
  },
  methods: {
    openEditReason(index) {
      this.editReasonIndex = index;
      this.editReasonValue = this.reasonsData[index];
    },
    openDeleteReason(index) {
      this.deleteReasonIndex = index;
    },
    closeDialog() {
      this.editReasonIndex = null;
      this.deleteReasonIndex = null;
      this.editReasonValue = '';
      this.showErrors = false;
    },
    async saveReason() {
      const reasons = [...this.reasonsData];
      const validated = await this.$refs.formValidator.validate();

      if (!validated || this.isSaving) {
        this.showErrors = true;

        return;
      }

      if (this.isEdit) {
        reasons[this.editReasonIndex] = this.editReasonValue;
      } else {
        reasons.push(this.editReasonValue);
      }

      this.saveReasons(reasons);
    },
    deleteReason() {
      const reasons = [...this.reasonsData];

      reasons.splice(this.deleteReasonIndex, 1);
      this.saveReasons(reasons);
    },

    saveReasons(reasons) {
      this.isSaving = true;
      const params = { reasons };
      const request = TimesheetsResource.update_entry_reasons({ organization_id: this.organizationId }, params);

      request
        .then(() => {
          this.reasonsData = reasons;
          let message = "Reason has been deleted.";

          if (!this.deleteReasonIndex) {
            message = this.isEdit ? "Reason has been updated." : "Reason has been added.";
          }

          this.$appMessage(message);
          this.closeDialog();
        })
        .catch((error) => {
          const message = error.response?.data?.error || 'An error occurred. Please try again';

          this.$appMessage(message);
        }).finally(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>
